import { Link as TanStackRouterLink, LinkProps as TanStackRouterLinkProps } from '@tanstack/react-router';
import { AnchorHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import { IconExternalLink } from '../../common/icons/cdl/ExternalLink';

import { linkStyles } from './linkStyles';

interface BaseLinkProps {
    size?: 'small' | 'default';
    children: ReactNode;
    external?: boolean;
}

type ExternalLinkProps = BaseLinkProps & {
    href: string;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'>;

type InternalLinkProps = BaseLinkProps & TanStackRouterLinkProps;

type LinkProps = ExternalLinkProps | InternalLinkProps;

const StyledExternalLink = styled.a<{ fontSize: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;

    ${linkStyles};
`;

const StyledInternalLink = styled(TanStackRouterLink as any)<{ fontSize: string }>`
    display: inline-block;

    ${linkStyles};
`;

export const Link = (props: LinkProps) => {
    const fontSize = props.size === 'small' ? '14px' : '16px';

    // Determine if this is an external link based on the presence of 'href' prop
    const isExternalLink = 'href' in props;

    if (isExternalLink) {
        const { external, size = 'default', children, ...externalProps } = props;
        const iconSize = size === 'small' ? 16 : 20;

        return (
            <StyledExternalLink {...externalProps} fontSize={fontSize} target="_blank" rel="noopener noreferrer">
                {children}
                {external ? <IconExternalLink width={iconSize} height={iconSize} style={{ flexShrink: 0 }} /> : null}
            </StyledExternalLink>
        );
    }

    // Need to destructure props to exclude them from passing them.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { external, size = 'default', children, ...routerProps } = props;

    return (
        <StyledInternalLink {...routerProps} fontSize={fontSize}>
            {children}
        </StyledInternalLink>
    );
};
